import { Observable, forkJoin, throwError, of } from 'rxjs';
import { MarkdownPayloadHelper } from '../content/markdown-payload.helper';
import { InsightsGeneratorHelper } from './insights-generator.helper';
import { InsightsStreamingHelper } from './insights-streaming.helper';

export class InsightsApiHelper {

  static fetchInsightsAsObservable(comp: any, mcode: number, opData: any, msgId: string, orchData?: any): Observable<any> {
    if (!opData) return throwError(() => new Error('No operation data provided'));

    return new Observable(obs => {
      // 1. Build Markdown for analytical agents with robust data resolution
      let markdown = '';
      try {
        const outputResponse = opData.response || opData;
        let dataArr: any[] | null = null;

        let targetData = outputResponse.Data || outputResponse.result?.Data || outputResponse;
        if (targetData) {
          if (typeof targetData === 'string') {
            try {
              dataArr = JSON.parse(targetData);
            } catch { }
          } else if (Array.isArray(targetData)) {
            dataArr = targetData;
          }
        }

        if (mcode === 1) {
          if (Array.isArray(dataArr)) {
            markdown = InsightsGeneratorHelper.buildSnapshotMarkdownForAPI(dataArr, comp.isTotalBase, comp.selectedWeight, Number(comp.insufficientBase || 0));
          } else if (opData.metric || (opData.response && opData.response.metric)) {
            markdown = InsightsGeneratorHelper.buildSnapshotMarkdownFromWidget(opData.response || opData);
          }
        } else {
          const crossTabData = dataArr || (opData.response || opData);
          if (Array.isArray(crossTabData)) {
            markdown = InsightsGeneratorHelper.buildCrosstabMarkdownForAPI(crossTabData, comp.isTotalBase, comp.selectedWeight, Number(comp.insufficientBase || 0));
          }
        }
      } catch (err: any) {
        console.error(`[FETCH-INSIGHTS] Markdown generation failed for mcode: ${mcode}`, err);
      }

      if (!markdown) {
        obs.error(`Markdown generation failed for mcode: ${mcode}`);
        return;
      }

      // Check if markdown has valid non-NA data
      if (!InsightsGeneratorHelper.hasValidDataInMarkdown(markdown)) {
        const dummyMsg = 'Metric values are zero or null for the selected criteria.';
        const dummyResponse = mcode === 1 ? {
          insightsVal: [dummyMsg],
          qnaText: dummyMsg,
          standard_insights: dummyMsg,
          trend_insights: dummyMsg,
          rawInsights: dummyMsg
        } : {
          insightsVal: [dummyMsg],
          qnaText: dummyMsg,
          rawInsights: dummyMsg
        };

        if (!comp.multiViewInsightsCache[msgId]) comp.multiViewInsightsCache[msgId] = {};
        comp.multiViewInsightsCache[msgId][mcode] = dummyResponse;

        obs.next(dummyResponse);
        obs.complete();
        return;
      }

      // 2. Stream Accumulation Helper: Accumulates chunks and errors on any non-200/Done response
      const streamToObs = (stream$: Observable<any>) => new Observable<string>(sObs => {
        let text = '';
        let hasError = false;
        stream$.subscribe({
          next: (ev) => {
            if (ev.event === 'error') {
              hasError = true;
              console.error('[STREAM DEBUG] Stream error event:', ev.data);
              sObs.error(ev.data?.message || 'Stream error event');
            } else {
              const extracted = comp.extractTextFromStreamEvent(ev);
              if (extracted && extracted !== 'done') {
                text += extracted;
              }
            }
          },
          complete: () => {
            if (hasError) return;
            if (!text.trim()) sObs.error('Empty stream response');
            else { sObs.next(text); sObs.complete(); }
          },
          error: (err: any) => sObs.error(err)
        });
      });

      // 3. Trigger Streams concurrently via forkJoin
      comp.getBearerToken().then((token: any) => {
        let basePayload: any = {
          client_name: comp.selected_client || comp.landingdata?.client_code || '',
          study: comp.selected_study || comp.landingdata?.study || '',
          session_id: comp.guidChatId,
          bearer_token: token,
          mcode: mcode,
          markdown: markdown,
          query: comp.currentQuestion || comp.inputValue || 'Analyze data',
          widget_details: (mcode === 1) ? {
            widget_name: opData.widget_name || opData.Widget_Name || opData.result?.widget_name || opData.result?.Widget_Name || opData.response?.widget_name || opData.response?.Widget_Name || comp.widget_Name || '',
            widget_id: opData.widget_id || opData.Widget_ID || opData.result?.widget_id || opData.result?.Widget_ID || opData.response?.widget_id || opData.response?.Widget_ID || comp.widget_Id || ''
          } : null
        };

        if (orchData && mcode > 1) {
          basePayload = { ...orchData, ...basePayload };
        }

        const docId = orchData?.selected_doc_id || opData?.selected_doc_id || opData?.response?.selected_doc_id || comp.promptData?.selected_doc_id;
        if (docId) basePayload.selected_doc_id = docId;

        // Ensure widget details are present for Snapshot
        if (mcode === 1 && !basePayload.widget_details?.widget_id) {
          basePayload.widget_details = {
            widget_name: orchData?.Widget_Name || orchData?.widget_name || opData?.Widget_Name || opData?.widget_name || comp.widget_Name || '',
            widget_id: orchData?.Widget_ID || orchData?.widget_id || opData?.Widget_ID || opData?.widget_id || comp.widget_Id || ''
          };
        }

        if (!comp.inFlightInsights[msgId]) comp.inFlightInsights[msgId] = new Set();
        comp.inFlightInsights[msgId].add(mcode);

        const streamTasks: { [key: string]: Observable<string> } = {};

        if (mcode === 1) {
          streamTasks['standard'] = streamToObs(comp.apiService.snapshotSummaryAgentStream({ ...basePayload, is_trend: false }));
          streamTasks['trend'] = streamToObs(comp.apiService.snapshotSummaryAgentStream({ ...basePayload, is_trend: true }));
          streamTasks['qna'] = streamToObs(comp.apiService.snapshotQaAgentStream(basePayload));
        } else {
          const summary$ = mcode === 2 ? comp.apiService.brandAnalysisSummaryAgentStream(basePayload) : comp.apiService.crosstabSummaryAgentStream(basePayload);
          const qna$ = mcode === 2 ? comp.apiService.brandAnalysisQaAgentStream(basePayload) : comp.apiService.crosstabQaAgentStream(basePayload);
          streamTasks['summary'] = streamToObs(summary$);
          streamTasks['qna'] = streamToObs(qna$);
        }

        forkJoin(streamTasks).subscribe({
          next: (results: any) => {
            if (!comp.multiViewInsightsCache[msgId]) comp.multiViewInsightsCache[msgId] = {};

            if (mcode === 1) {
              const combinedRaw = `${results.standard}[[[SPLIT]]]${results.trend}`;
              comp.multiViewInsightsCache[msgId][mcode] = {
                insightsVal: comp.formatInsightsIntoPoints(combinedRaw),
                qnaText: results.qna,
                standard_insights: results.standard,
                trend_insights: results.trend,
                rawInsights: combinedRaw
              };
            } else {
              comp.multiViewInsightsCache[msgId][mcode] = {
                insightsVal: comp.formatInsightsIntoPoints(results.summary),
                qnaText: results.qna,
                rawInsights: results.summary
              };
            }
            comp.inFlightInsights[msgId].delete(mcode);
            obs.next(comp.multiViewInsightsCache[msgId][mcode]);
            obs.complete();
          },
          error: (err: any) => {
            comp.inFlightInsights[msgId].delete(mcode);
            obs.error(err);
          }
        });

      }).catch((err: any) => {
        if (comp.inFlightInsights[msgId]) comp.inFlightInsights[msgId].delete(mcode);
        obs.error(err);
      });
    });
  }


  static async callInsightsAPI(comp: any, 
    mcode: number,
    markdown: string,
    query?: string,
    widget_details?: any,
    targetBlock?: any
  ): Promise<void> {
    if (comp.failedCase) {
      console.warn('[ORCHESTRATION] callInsightsAPI skipped: failedCase is true (preceding error detected)');
      return;
    }

    // [UNBLOCK UI] Hide the AI loader so the UI can immediately plot the data while insights stream!
    comp.stopAiLoader();
    comp.arktabaccess = false;

    comp.insightsStreamingText = '';
    comp.insightsStreamingPoints = [];
    comp.isInsightspopupOpen = true;
    comp.insightsVal = '';
    comp.setFailedCaseState(false, targetBlock);

    const innerMsgId = targetBlock?.messageId || comp.selectedMessageId || 'current';
    if (!comp.inFlightInsights[innerMsgId]) {
      comp.inFlightInsights[innerMsgId] = new Set();
    }
    comp.inFlightInsights[innerMsgId].add(mcode);

    try {
      // Determine default active view for snapshots:
      // (1 brand AND 1 market) -> Trend View is best for temporal analysis
      // (Multi-brand OR Multi-market) -> Normal View is best for cross-comparison
      const bArray = comp.promptData?.Primary_Brands || comp.promptData?.primary_brands || comp.promptData?.Brands || comp.promptData?.brands || [];
      const mArray = comp.promptData?.Markets || comp.promptData?.markets || [];
      const isSingleFocus = (bArray.length === 1 && (mArray.length <= 1));

      if (mcode === 1 && comp.filteredWidgetData?.trendline_response && isSingleFocus) {
        comp.isTrendActive = true;
      } else {
        comp.isTrendActive = false;
      }

      let basePayload = await MarkdownPayloadHelper.buildMarkdownPayload(comp);
      if (!basePayload) {
        throw new Error('Payload construction failed');
      }

      // Merge specialized payload (Row, Column, etc.) for non-primary modules so the API has the required data structures
      if (comp.chatPayload && mcode > 1) {
        basePayload = { ...comp.chatPayload, ...basePayload };
      }

      if (!markdown || !InsightsGeneratorHelper.hasValidDataInMarkdown(markdown)) {
        comp.setFailedCaseState(true, targetBlock);
        comp.errorHeader = 'Analysis Status:';
        comp.insightsVal = ['Metric values are zero or null for the selected criteria.'];
        comp.InsightsResponse = {
          header: comp.errorHeader,
          message: comp.insightsVal[0],
          show: true,
        };
        comp._insightsSerive.showInisghts(comp.InsightsResponse);
        comp.updateMarkdownTextRealTime(comp.insightsVal[0]);
        comp.stopAiLoader();
        comp.isInsightspopupOpen = true;
        comp.arktabaccess = false;

        if (comp.inFlightInsights[innerMsgId]) {
          comp.inFlightInsights[innerMsgId].delete(mcode);
        }
        return;
      }


      comp.insightsVal = [];
      comp.insightsStreamingText = '';
      comp.insightsStreamingPoints = [];
      comp.isInsightspopupOpen = true;
      comp.stopInsightsAutoScroll();

      comp.InsightsResponse = {
        header: 'Insights (Connecting...)',
        message: 'Connecting to specialized intelligence stream...',
        show: true,
      };
      comp._insightsSerive.showInisghts(comp.InsightsResponse);

      let insightsResponse = '';
      let qnaResponse = '';
      let insightsCompleted = false;
      let qnaCompleted = false;
      let firstChunkReceived = false;

      // Reset specific snapshot text storage
      comp.snapshotinsights_standard_text = '';
      comp.snapshotinsights_trendline_text = '';

      // Check if we need dual calls for snapshot trend context
      const isSnapshotTrend = (mcode === 1 && !!comp.filteredWidgetData?.trendline_response);
      let standardDone = false;
      let trendDone = !isSnapshotTrend;

      const finalizeDualInsights = () => {
        if (standardDone && trendDone) {
          const combinedRaw = (comp.snapshotinsights_standard_text || "No Data Available").trim() +
            "[[[SPLIT]]]" +
            (comp.snapshotinsights_trendline_text || "No Data Available").trim();
          const jsonFormatted = JSON.stringify({ "insights": { "Value": combinedRaw } });

          comp.pushChatResponseEntry('getSnapshotInsights', jsonFormatted);

          comp.isInsightsStreaming = false;
          setTimeout(() => {
            comp.scrollInsightsToTopSmooth();
          }, 1000);

          insightsCompleted = true;
          comp.checkStreamCompletion(insightsCompleted, qnaCompleted, jsonFormatted, qnaResponse, mcode, targetBlock);
        }
      };

      if (isSnapshotTrend) {
        // --- CALL 1: Standard Summary ---
        comp.apiService.snapshotSummaryAgentStream({ ...basePayload, stream: true }).subscribe({
          next: (event: any) => {
            const text = comp.extractTextFromStreamEvent(event);
            if (text && text !== 'done') {
              comp.snapshotinsights_standard_text += text;
              if (!comp.isTrendActive) {
                if (!firstChunkReceived) {
                  firstChunkReceived = true;
                  comp.InsightsResponse = { header: 'Insights (Streaming...)', message: 'Generating standard insights...', show: true };
                  comp._insightsSerive.showInisghts(comp.InsightsResponse);
                }
                insightsResponse = comp.snapshotinsights_standard_text;
                comp.processInsightsChunkRealTime(text, insightsResponse);
              }
            }
          },
          error: (err: any) => {
            console.error('[Standard Insights Error]:', err);
            comp.snapshotinsights_standard_text += '\n[Standard Insights Error]\n';
            if (!comp.isTrendActive) {
              insightsCompleted = true;
              comp.checkStreamCompletion(insightsCompleted, qnaCompleted, comp.snapshotinsights_standard_text, qnaResponse, mcode, targetBlock);
            }
          },
          complete: () => {
            standardDone = true;

            if (!comp.isTrendActive) {
              const finalStandard = comp.snapshotinsights_standard_text;
              insightsResponse = finalStandard;
              comp.isInsightsStreaming = false; // Reset streaming flag
              comp.processInsightsChunkFinal(finalStandard);
              // Show UI as done, but let finalizeDualInsights handle history/completion if needed
            }
            finalizeDualInsights();
          }
        });

        // --- CALL 2: Trend Summary ---
        (async () => {
          const trendBasePayload = await MarkdownPayloadHelper.buildMarkdownPayload(comp, true); // Get trend markdown
          comp.apiService.snapshotSummaryAgentStream({ ...trendBasePayload, stream: true }).subscribe({
            next: (event: any) => {
              const text = comp.extractTextFromStreamEvent(event);
              if (text && text !== 'done') {
                comp.snapshotinsights_trendline_text += text;
                if (comp.isTrendActive) {
                  if (!firstChunkReceived) {
                    firstChunkReceived = true;
                    comp.InsightsResponse = { header: 'Trend Insights (Streaming...)', message: 'Generating trend analysis...', show: true };
                    comp._insightsSerive.showInisghts(comp.InsightsResponse);
                  }
                  insightsResponse = comp.snapshotinsights_trendline_text;
                  comp.processInsightsChunkRealTime(text, insightsResponse);
                }
              }
            },
            error: (err: any) => {
              console.error('[Trend Insights Error]:', err);
              comp.snapshotinsights_trendline_text += '\n[Trend Insights Error]\n';
              if (comp.isTrendActive) {
                insightsCompleted = true;
                comp.checkStreamCompletion(insightsCompleted, qnaCompleted, comp.snapshotinsights_trendline_text, qnaResponse, mcode, targetBlock);
              }
            },
            complete: () => {
              trendDone = true;

              if (comp.isTrendActive) {
                const finalTrend = comp.snapshotinsights_trendline_text;
                insightsResponse = finalTrend;
                comp.processInsightsChunkFinal(finalTrend);
              }
              finalizeDualInsights();
            }
          });
        })();

      } else {
        // 1. Single Summary Agent logic
        let summaryObs$: Observable<any> | null = null;
        const summaryPayload = { ...basePayload, stream: true };

        if (mcode === 1) summaryObs$ = comp.apiService.snapshotSummaryAgentStream(summaryPayload);
        else if (mcode === 2) summaryObs$ = comp.apiService.brandAnalysisSummaryAgentStream(summaryPayload);
        else if (mcode === 3) summaryObs$ = comp.apiService.crosstabSummaryAgentStream(summaryPayload);

        if (summaryObs$) {
          summaryObs$.subscribe({
            next: (event: any) => {
              if (!firstChunkReceived) {
                firstChunkReceived = true;
                comp.InsightsResponse = {
                  header: 'Insights (Streaming...)',
                  message: 'AI is generating insights in real-time...',
                  show: true,
                };
                comp._insightsSerive.showInisghts(comp.InsightsResponse);
              }

              const text = comp.extractTextFromStreamEvent(event);

              if (text && text !== 'done') {
                insightsResponse += text;
                comp.processInsightsChunkRealTime(text, insightsResponse);
              }
            },
            error: (err: any) => {
              console.error('[Insights Stream Error]:', err);
              insightsResponse += '\n[Insights Error]\n';
              insightsCompleted = true;
              comp.insightsVal = ['Insights Generation failed.'];
              comp.updateInsightsPopupFinal();
              comp.checkStreamCompletion(insightsCompleted, qnaCompleted, insightsResponse, qnaResponse, mcode, targetBlock);
            },
            complete: () => {
              insightsCompleted = true;
              if (insightsResponse && !comp.insightsStreamingText) comp.insightsStreamingText = insightsResponse;
              comp.processInsightsChunkFinal(comp.insightsStreamingText || insightsResponse);

              let formattedInsights = comp.insightsStreamingText || insightsResponse;
              if (mcode === 1) {
                formattedInsights = JSON.stringify({ "insights": { "Value": (formattedInsights || "No Data Available").trim() } });
              }
              comp.pushChatResponseEntry((mcode === 1 ? 'getSnapshotInsights' : 'getInsights'), formattedInsights);
              comp.checkStreamCompletion(insightsCompleted, qnaCompleted, formattedInsights, qnaResponse, mcode, targetBlock);
            }
          });
        }
      }

      // 2. Q&A Agent - Always streaming for real-time interaction
      let qnaObs$: Observable<any> | null = null;
      const qnaPayload = { ...basePayload, stream: true };

      if (mcode === 1) qnaObs$ = comp.apiService.snapshotQaAgentStream(qnaPayload);
      else if (mcode === 2) qnaObs$ = comp.apiService.brandAnalysisQaAgentStream(qnaPayload);
      else if (mcode === 3) qnaObs$ = comp.apiService.crosstabQaAgentStream(qnaPayload);

      if (qnaObs$) {
        qnaObs$.subscribe({
          next: (event: any) => {
            // if (comp.arktabaccess) {
            //   comp.stopAiLoader();
            //   comp.arktabaccess = false;
            // }

            let text = '';
            if (event.event === 'text_delta') {
              text = event.data?.delta || '';
            } else if (typeof event.data === 'string') {
              text = event.data;
            } else if (event.data) {
              text = event.data.delta || event.data.chunk || event.data.Value || '';
            }

            if (text && text !== 'done') {
              qnaResponse += text;
              comp.updateMarkdownTextRealTime(qnaResponse);
            }
          },
          error: (err: any) => {
            console.error('[QnA Stream Error]:', err);
            qnaResponse += '\n[QnA Stream Error]\n';
            qnaCompleted = true;
            comp.checkStreamCompletion(insightsCompleted, qnaCompleted, insightsResponse, qnaResponse, mcode, targetBlock);
          },
          complete: () => {
            qnaCompleted = true;
            comp.pushChatResponseEntry('getQnA', qnaResponse);
            comp.checkStreamCompletion(insightsCompleted, qnaCompleted, insightsResponse, qnaResponse, mcode, targetBlock);
          }
        });
      }
    } catch (error: any) {
      console.error('[Insights Error]:', error);
      comp.insightsVal = ['Failed to authenticate or build payload.'];
      comp.stopAiLoader();
      comp.arktabaccess = false;
      const innerMsgId = targetBlock?.messageId || comp.selectedMessageId || 'current';
      if (comp.inFlightInsights[innerMsgId]) {
        comp.inFlightInsights[innerMsgId].delete(mcode);
      }
    }
  }


  static OnInsigthsbuttonClick(comp: any, isChatResponse = false, getSnapshotInsights?: any, getSnapshotInsightsTrend?: any, targetBlock?: any) {
    let WidgetId = comp.Widget_Id;
    comp.arktabaccess = false;
    let data = [];
    comp.InsightsResponse = [];
    let ShareColumn = (comp.selectedDataFormat != 'Respondent Level' ? 'value' : (comp.isTotalBase
      ? (comp.selectedWeight == "Unweighted" ? 'totalUnweightedShare' : 'totalWeightedShare')
      : (comp.selectedWeight == "Unweighted" ? 'answerUnweightedShare' : 'answerWeightedShare')));
    let BaseColumn = !comp.isTotalBase
      ? 'totalUnweightedBase'
      : 'answerUnweightedBase';

    if (!comp.Snapshotdataforexport) {
      comp.Snapshotdataforexport = comp._configService.getDropdownValues();
    }

    comp.Snapshotdataforexport.selectionSummary = comp.selectionSummary;
    comp.isInsightspopupOpen = true;
    if (getSnapshotInsights && isChatResponse) {
      // Re-normalize everything into component properties for view consistency:
      let rawText = '';
      if (typeof getSnapshotInsights === 'string') rawText = getSnapshotInsights;
      else if (getSnapshotInsights?.insights?.Value) rawText = getSnapshotInsights.insights.Value;
      else if (getSnapshotInsights?.Value) rawText = getSnapshotInsights.Value;

      if (rawText.includes('[[[SPLIT]]]')) {
        const parts = rawText.split('[[[SPLIT]]]');
        comp.snapshotinsights_standard_text = parts[0];
        comp.snapshotinsights_trendline_text = parts[1];
      } else {
        comp.snapshotinsights_standard_text = rawText;
        // Check if there was separate trend insights (previous migration format)
        if (getSnapshotInsightsTrend) {
          if (typeof getSnapshotInsightsTrend === 'string') comp.snapshotinsights_trendline_text = getSnapshotInsightsTrend;
          else if (getSnapshotInsightsTrend?.insights?.Value) comp.snapshotinsights_trendline_text = getSnapshotInsightsTrend.insights.Value;
          else if (getSnapshotInsightsTrend?.Value) comp.snapshotinsights_trendline_text = getSnapshotInsightsTrend.Value;
        }
      }

      const targetText = comp.isTrendActive ? (comp.snapshotinsights_trendline_text || comp.snapshotinsights_standard_text) : comp.snapshotinsights_standard_text;
      comp.insightsVal = comp.formatInsightsIntoPoints(targetText);

      // Populate multiViewInsightsCache immediately for history consistency
      const msgId = targetBlock?.messageId || comp.selectedMessageId || 'current';

      if (!comp.multiViewInsightsCache[msgId]) comp.multiViewInsightsCache[msgId] = {};
      const combinedRaw = (comp.snapshotinsights_standard_text || '').trim() + "[[[SPLIT]]]" + (comp.snapshotinsights_trendline_text || '').trim();

      comp.multiViewInsightsCache[msgId][comp.mcode] = {
        insightsVal: comp.insightsVal,
        qnaText: comp.chatPreviewBlocks.find((b: any) => b.messageId === msgId)?.QuestionAnswer || '',
        standard_insights: comp.snapshotinsights_standard_text,
        trend_insights: comp.snapshotinsights_trendline_text,
        rawInsights: combinedRaw
      };
    } else {
      comp.insightsVal = (getSnapshotInsights && !isChatResponse) ? comp.formatInsightsIntoPoints(getSnapshotInsights) : '';
    }

    (getSnapshotInsights && isChatResponse ? of(getSnapshotInsights) :
      comp.apiService.getInsightsData(
        comp.isIndexSelected,
        comp.MainResult,
        comp.selected_study,
        comp.selected_client,
        comp.selectionSummary,
        comp.confidenceLevel,
        ShareColumn,
        BaseColumn,
        comp._configService.getDropdownValues().compareAcross,
        "",
        "",
        WidgetId,
        localStorage.getItem('useremail') ?? '',
        '',
        'ARK'
      ))
      .subscribe({
        next: (result: string) => {
          // Requirement: Save insights data to Elasticsearch chat history for mcode==1 (snapshot module)
          if (!isChatResponse) {
            let snapshotInsightsText = 'No data Available';
            try {
              if (result && typeof result === 'string') {
                let insightsValue = result;
                try {
                  const parsed = JSON.parse(result);
                  const firstKey = Object.keys(parsed)[0];
                  if (firstKey && parsed[firstKey] && parsed[firstKey].Value) {
                    insightsValue = parsed[firstKey].Value;
                  }
                } catch (parseError) {
                }
                const insights = insightsValue.split('- ').map(item => item.trim()).filter(item => item.length > 0);
                if (insights.length > 0) {
                  snapshotInsightsText = insights.slice(0, 2).map(insight => {
                    let cleaned = insight.replace(/\*\*/g, ''); // Remove bold asterisks
                    cleaned = cleaned.replace(/^[^:]+:\s*/, ''); // Remove header before colon
                    cleaned = cleaned.replace(/\\n/g, ' '); // Remove escaped newlines
                    cleaned = cleaned.replace(/\n/g, ' '); // Remove actual newlines
                    return cleaned.trim();
                  }).join(' ');
                }
              }
            } catch (e) {
              console.log('Error extracting snapshot insights:', e);
            }
            comp.updateQuestionAnswerForSelectedMessage(snapshotInsightsText);
            comp.pushChatResponseEntry('getSnapshotInsights', result);
            comp.saveChatList(
              targetBlock ? targetBlock.question : (comp.chatPreviewBlocks.length > 0 ? comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1].question : comp.inputValue),
              comp.chatResponse,
              snapshotInsightsText,
              targetBlock?.messageId
            );
            comp.chatResponse = [];
          }
          comp.showSnapshot = true;

          if (!isChatResponse) {
            let parsedResult: any;

            try {
              parsedResult = JSON.parse(result);
            } catch {
              parsedResult = null;
            }

            if (parsedResult && !parsedResult.success) {
              comp.insightsVal = comp.formatInsightsIntoPoints(parsedResult.message || "No Data Available");
            }
            else if (result && result.trim() !== "") {
              let parsedInsightsObj = JSON.parse(result);
              let combinedInsightsText = '';
              for (const section in parsedInsightsObj) {
                if (parsedInsightsObj[section] && parsedInsightsObj[section].Value) {
                  combinedInsightsText += parsedInsightsObj[section].Value + '\n';
                }
              }
              comp.insightsVal = comp.formatInsightsIntoPoints(combinedInsightsText);
            } else {
              comp.insightsVal = ["No Data Available"];
            }
            comp.InsightsResponse = {
              header: comp.failedCase ? (comp.errorHeader || 'Error:') : 'Insights',
              message: comp.insightsVal.join('\n'),
              show: true
            };
            comp.stopAiLoader();
            comp.arktabaccess = false; // Stop loader
          }
          if (isChatResponse) {
            let parsedResult: any;
            let combinedInsightsText = '';
            let insightsFound = false;

            if (typeof result === "string") {
              try {
                parsedResult = JSON.parse(result);
              } catch (e) {
                // If it's a plain string (possibly with [[[SPLIT]]]), use it directly
                combinedInsightsText = result;
                insightsFound = true;
                parsedResult = null;
              }
            } else if (typeof result === "object") {
              parsedResult = result;
            }

            if (parsedResult && typeof parsedResult === 'object') {
              for (const section in parsedResult) {
                if (parsedResult[section] && parsedResult[section].Value) {
                  combinedInsightsText += (combinedInsightsText ? '\n' : '') + parsedResult[section].Value;
                  insightsFound = true;
                }
              }
            }

            if (insightsFound && combinedInsightsText) {
              // Ensure snapshot dual-text properties are updated if the result contained the split marker
              if (combinedInsightsText.includes('[[[SPLIT]]]')) {
                const parts = combinedInsightsText.split('[[[SPLIT]]]');
                comp.snapshotinsights_standard_text = parts[0];
                comp.snapshotinsights_trendline_text = parts[1];
              }

              comp.insightsVal = comp.formatInsightsIntoPoints(combinedInsightsText);

              // Update cache with the resolved points
              const msgId = targetBlock?.messageId || comp.selectedMessageId || 'current';
              if (comp.multiViewInsightsCache[msgId]?.[comp.mcode]) {
                comp.multiViewInsightsCache[msgId][comp.mcode].insightsVal = comp.insightsVal;
                if (combinedInsightsText.includes('[[[SPLIT]]]')) {
                  comp.multiViewInsightsCache[msgId][comp.mcode].standard_insights = comp.snapshotinsights_standard_text;
                  comp.multiViewInsightsCache[msgId][comp.mcode].trend_insights = comp.snapshotinsights_trendline_text;
                  comp.multiViewInsightsCache[msgId][comp.mcode].rawInsights = combinedInsightsText;
                }
              }
            } else {
              comp.insightsVal = ["No Data Available"];
            }

            comp.InsightsResponse = {
              header: comp.failedCase ? (comp.errorHeader || 'Error:') : 'Insights',
              message: Array.isArray(comp.insightsVal) ? comp.insightsVal.join('\n') : String(comp.insightsVal),
              show: true
            };
          }
          comp._insightsSerive.showInisghts(comp.InsightsResponse);
          comp.stopAiLoader();
          comp.arktabaccess = false;
        },
        error: (error: any) => {
          console.error('Error fetching insights for snapshot:', error);
          comp.insightsVal = ['Failed to generate insights. Please try again.'];
          comp.setFailedCaseState(true, targetBlock);
          comp.stopAiLoader();
          comp.arktabaccess = false;
        }
      });
  }


}
